"use client";
import { useState, useMemo } from "react";
import CustomSelect from "../design1/components/ui/form/CustomSelect";
import Link from "next/link";

import { useFetchValidSocialMediaLinks } from "src/app/lib/useFetcchValidSocialMediaLinks";
import Switch from "./components/ButtonSwitch";
import { useFetchCompanyInfo } from "src/app/lib/fetchCompanyInfo";
import Skeleton from "react-loading-skeleton";
import HeroPage2 from "./components/HeroPage2";
import CardGrid from "../../CardGrid";
import { headingTags, MemberType } from "src/types";
import ContactForm from "../design1/components/ContactUsForm";
import { ParentCard } from "../design1/PropertyPage1";
import { newData } from "../design1/components";
import React from "react";
import { useCompanyStaff } from "src/app/lib/useCompanyStaff";
import Image from "next/image";
import { generateSlugWithId } from "src/app/serverUtils";
import { VerifiedBadge } from "../../Icon";
import { useDirectors } from "src/app/lib/useCompanyDirector";

const ContactUs2 = () => {
  const [activeBranch, setActiveBranch] = useState<1 | 2>(1); // Default is 1
  const { data, isLoading: loadingInfo } = useFetchCompanyInfo();
  const services = data?.companyServices;

  const filteredServices = React.useMemo(
    () => newData.filter((service) => services?.includes(service.assessor)),
    []
  );

  return (
    <section className="max-w-full pb-12 overflow-clip">
      <HeroPage2 bgSize="cover" bgImage="/images/wavy-lines.webp">
        <div className="wrapper text-white flex flex-col items-center">
          <h1 className="mb-4">Contacts & Services</h1>
          <p>
            Explore our range of professional real estate services designed to
            meet your every need.
          </p>
        </div>
      </HeroPage2>
      <div className="wrapper py-10">
        {activeBranch === 1 && (
          <div className="grid-container !lg:grid-cols-1 relative">
            <div className="first flex flex-col gap-4">
              <Switch
                disable={filteredServices.length < 1 ? 2 : 0}
                button1Text="Contacts"
                button2Text="Services"
                activeState={activeBranch}
                onSwitch={(newState) => {
                  if (newState === 2 && filteredServices.length < 1) {
                    return null;
                  }
                  setActiveBranch(newState);
                }}
              />
              <ParentCard headingTag="h3" variant header="Contact Details">
                <ContactForm email={data?.companyInfo?.email as string} />
              </ParentCard>
              <Contact
                loading={loadingInfo}
                email={data?.companyInfo?.email}
                address={data?.companyInfo?.head_office_address}
                phone={data?.companyInfo?.phone_number}
              />
            </div>
            <SecondCol />
          </div>
        )}
        {activeBranch === 2 && (
          <div className="">
            <Switch
              button1Text="Contacts"
              button2Text="Services"
              activeState={activeBranch}
              onSwitch={(newState) => setActiveBranch(newState)}
            />
            <div className="mt-8">
              {filteredServices && filteredServices.length > 0 ? (
                <CardGrid minCardWidth={350} gap={20} maxColumns={3}>
                  {filteredServices.map((item, index) => {
                    return <Card2 className="!w-full" key={index} {...item} />;
                  })}
                </CardGrid>
              ) : (
                <div>No data available</div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
export default ContactUs2;

const SecondCol = () => {
  const { data: staffData, isLoading: staffLoading } = useCompanyStaff();
  const { data: directorsData, isLoading: directorsLoading } = useDirectors();
  const isLoading = staffLoading || directorsLoading;

  const options = useMemo(() => {
    const branches = staffData?.staff?.map((item) => item.branch);
    const uniqueBranches = new Set(branches);
    return Array.from([...uniqueBranches, "All branches", "Directors"]).map(
      (branch) => ({
        label: branch as string,
        value: branch as string,
      })
    );
  }, [staffData]);

  const [selectedValue, setSelectedValue] = useState<string | null>(
    "Directors"
  );

  const members = useMemo(() => {
    if (!selectedValue || selectedValue === "Directors") {
      return directorsData || [];
    }
    if (selectedValue === "All branches") {
      return staffData?.staff || [];
    }
    return (
      staffData?.staff?.filter((item) => item.branch === selectedValue) || []
    );
  }, [selectedValue, staffData?.staff, directorsData]);

  // Efficient handler for select change
  const handleSelect = (value: string) => {
    setSelectedValue(value); // Updating state

  };
  return (
    <div className="items max-h-[600px] !h-max sticky top-[75px] bg-white rounded-xl shadow-md ">
      <div className="first px-2 py-2 pt-2 flex justify-between">
        <h3 className="h5">
          {selectedValue === "Directors"
            ? "Company Directors"
            : "Company Staff"}
        </h3>
        {members && members.length > 0 && (
          <div className="border rounded-md border-themeColor p-2">
            <CustomSelect
              options={options}
              value={selectedValue}
              onChange={setSelectedValue}
              handleSelect={handleSelect}
              placeholder="All branches"
            />
          </div>
        )}
      </div>
      <div className=" h-max  max-h-[600px] bg-white rounded-xl scrollbar-hide overflow-y-auto w-full">
        {/* Header */}
        {/* Scrollable content */}
        <div className="">
          {isLoading ? (
            <Skeleton count={8} height={120} className="w-full" />
          ) : members && members.length > 0 ? (
            <CardGrid maxColumns={1}>
              {members.map((item, index) => (
                <ContactCard as="h4" key={index} {...item} />
              ))}
            </CardGrid>
          ) : (
            <div className="p-3">
              {" "}
              No staff information attached yet. Please try again later.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export interface ExtendedContactCard extends MemberType {
  variant?: boolean;
  as?: headingTags;
}
export const ContactCard: React.FC<ExtendedContactCard> = ({
  name,
  id,
  email,
  phone,
  image,
  role,
  variant,
  is_verified,
  as = "h3",
}) => {
  const Tag = as;

  const url = generateSlugWithId(id, name);
  return (
    <div
      style={{
        boxShadow: "0px 2px 10px 0px rgba(33, 33, 33, 0.05)",
      }}
      className="flex mx-4 rounded-xl py-4 pl-4 gap-4"
    >
      <Image
        height={variant ? 121 : 96}
        width={variant ? 121 : 96}
        className={`rounded-full block object-cover h-[6rem] aspect-square shadow-lg ${
          variant && "!h-[7.6rem] rounded-md"
        }`}
        src={image || "/images/person-1.webp"}
        alt=""
      />
      <div className="items flex-1  text-secondary-main">
        <div className="first pb-4">
          <Tag>
            <Link
              href={`/staff/${url}`}
              className="text-secondary-main flex items-center gap-2 hover:underline h6 mb-2 font-medium"
            >
              <span className="truncate overflow-hidden whitespace-nowrap max-w-[220px] xlg:max-w-[160px]">
                {name}
              </span>
              <VerifiedBadge verified={is_verified as boolean} />
            </Link>
          </Tag>

          <h5 className="text-highLight-main h6 font-medium capitalize">
            {role}
          </h5>
        </div>

        <div className="second text-sm flex pt-3 flex-col">
          {phone && (
            <span className="flex items-center gap-1">
              <CallIcon />
              <Link className="hover:underline" href={`tel:${phone}`}>
                {phone}
              </Link>
            </span>
          )}

          <span className="flex items-center gap-1">
            <span className="flex items-center gap-1">
              <EmailIcon />
              <Link
                className="hover:underline truncate block w-full overflow-hidden whitespace-nowrap max-w-[260px] xlg:max-w-[180px] md:!max-w-[150px]"
                href={`mailto:${email}`}
              >
                {email}
              </Link>
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};
const CallIcon = () => {
  return (
    <svg
      width="14"
      height="15"
      viewBox="0 0 14 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M10.034 13.7569C9.07402 13.7216 6.35335 13.3456 3.50402 10.4969C0.655349 7.64761 0.280015 4.92761 0.244015 3.96694C0.190682 2.50294 1.31202 1.08094 2.60735 0.525609C2.76333 0.458253 2.93415 0.432609 3.10304 0.451192C3.27192 0.469776 3.43308 0.531948 3.57068 0.631609C4.63735 1.40894 5.37335 2.58494 6.00535 3.50961C6.1444 3.71276 6.20386 3.95997 6.17239 4.20414C6.14091 4.44831 6.02071 4.67236 5.83468 4.83361L4.53402 5.79961C4.47118 5.84499 4.42694 5.91163 4.40953 5.98716C4.39212 6.06269 4.40272 6.14197 4.43935 6.21028C4.73402 6.74561 5.25802 7.54294 5.85802 8.14294C6.45802 8.74294 7.29335 9.30161 7.86602 9.62961C7.93782 9.66992 8.02233 9.68118 8.10219 9.66109C8.18204 9.64101 8.25116 9.59109 8.29535 9.52161L9.14202 8.23294C9.29767 8.02617 9.5273 7.88765 9.7828 7.8464C10.0383 7.80514 10.2999 7.86434 10.5127 8.01161C11.4507 8.66094 12.5453 9.38428 13.3467 10.4103C13.4544 10.5489 13.523 10.7139 13.5451 10.888C13.5673 11.0622 13.5423 11.2391 13.4727 11.4003C12.9147 12.7023 11.5027 13.8109 10.034 13.7569Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};
export const EmailIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.6673 5.02344V11.3334C14.6673 11.8436 14.4724 12.3344 14.1225 12.7056C13.7725 13.0768 13.2939 13.3002 12.7847 13.3301L12.6673 13.3334H3.33398C2.82384 13.3335 2.33297 13.1386 1.96181 12.7886C1.59065 12.4386 1.36725 11.96 1.33732 11.4508L1.33398 11.3334V5.02344L7.63065 9.22144L7.70798 9.26544C7.79912 9.30997 7.89922 9.33311 8.00065 9.33311C8.10208 9.33311 8.20218 9.30997 8.29332 9.26544L8.37065 9.22144L14.6673 5.02344Z"
        fill="var(--primary-color)"
      />
      <path
        d="M12.6675 2.66797C13.3875 2.66797 14.0189 3.04797 14.3709 3.6193L8.00086 7.86597L1.63086 3.6193C1.79805 3.34785 2.02772 3.1203 2.30071 2.95563C2.5737 2.79095 2.88209 2.69393 3.20019 2.67264L3.33419 2.66797H12.6675Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};
const Contact: React.FC<{
  phone?: string[];
  email?: string;
  address?: string;
  loading?: boolean;
}> = ({ phone, address, email, loading }) => {
  const { socialMediaLinks, loading: isLoading } =
    useFetchValidSocialMediaLinks();

  return (
    <ParentCard headingTag="h3" variant header="Head Office">
      <div className="flex-col xs:gap-7 flex gap-4">
        {loading ? (
          <Skeleton count={3} className="w-full block my-1" height={80} />
        ) : (
          <>
            {Array.isArray(phone) && phone.length > 0 && (
              <CardInfo icon={<BillCallIcon />} title="Phone Number">
                {Array.isArray(phone) && phone.length > 0 ? (
                  <div className="flex flex-wrap gap-4">
                    {phone.map((phoneNumber, index) => (
                      <Link
                        key={index}
                        href={`tel:${phoneNumber}`}
                        className="hover:underline"
                      >
                        {phoneNumber}
                      </Link>
                    ))}
                  </div>
                ) : (
                  "No phone number available"
                )}
              </CardInfo>
            )}

            {address && (
              <CardInfo icon={<LocationIcon />} title="Location">
                <div className="flex gap-4">
                  {address ? (
                    <AddressLink address={address} />
                  ) : (
                    "No address available"
                  )}
                </div>
              </CardInfo>
            )}

            {email && (
              <CardInfo icon={<Message2Icon />} title="Email Address">
                {email ? <EmailLink email={email} /> : "No email available"}
              </CardInfo>
            )}
          </>
        )}

        {isLoading ? (
          <div className="flex gap-4">
            <Skeleton
              count={6}
              containerClassName="flex gap-4"
              className="inline-block"
              circle
              height={60}
              width={60}
            />
          </div>
        ) : socialMediaLinks.length > 0 ? (
          <CardInfo icon={<ShareIcon />} title="Follow Us">
            <div className="flex items-center gap-4">
              {socialMediaLinks?.map((item, index) => {
                return (
                  <Link href={item.url} key={index}>
                    {item.icon}
                  </Link>
                );
              })}
            </div>
          </CardInfo>
        ) : (
          ""
        )}
        {}
      </div>
    </ParentCard>
  );
};
export const CardInfo: React.FC<{
  title: string;
  icon: React.ReactElement<React.ReactNode>;
  children: React.ReactNode;
  variant?: boolean;
}> = ({ title, icon, children, variant }) => {
  return (
    <div className="flex items-center gap-4">
      <div
        style={{
          backgroundColor: "rgba(var(--primary-color-rgb), 0.1)",
        }}
        className={`first ${
          !variant && "border-themeColor  border"
        }  grid place-items-center aspect-square xs:h-[4rem] h-[6rem] rounded-full`}
      >
        <span className="scale-[1.2] xs:scale-[0.9]">{icon}</span>
      </div>
      <div className="second flex flex-col gap-2">
        <h4 className="first h5 text-secondary-main">{title}</h4>
        <div className="text-primary font-medium ">{children}</div>
      </div>
    </div>
  );
};
export const BillCallIcon = () => {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M27.5889 36.8884C25.1889 36.8001 18.3873 35.8601 11.2639 28.7384C4.14228 21.6151 3.20394 14.8151 3.11394 12.4134C2.98061 8.75345 5.78395 5.19845 9.02228 3.81012C9.41224 3.64173 9.83928 3.57762 10.2615 3.62407C10.6837 3.67053 11.0866 3.82596 11.4306 4.07511C14.0973 6.01845 15.9373 8.95845 17.5173 11.2701C17.8649 11.778 18.0136 12.396 17.9349 13.0064C17.8562 13.6169 17.5557 14.177 17.0906 14.5801L13.8389 16.9951C13.6818 17.1086 13.5713 17.2752 13.5277 17.464C13.4842 17.6528 13.5107 17.851 13.6023 18.0218C14.3389 19.3601 15.6489 21.3534 17.1489 22.8534C18.6489 24.3534 20.7373 25.7501 22.1689 26.5701C22.3484 26.6709 22.5597 26.699 22.7594 26.6488C22.959 26.5986 23.1318 26.4738 23.2423 26.3001L25.3589 23.0784C25.7481 22.5615 26.3222 22.2152 26.9609 22.1121C27.5997 22.0089 28.2535 22.1569 28.7856 22.5251C31.1306 24.1484 33.8673 25.9568 35.8706 28.5218C36.14 28.8683 36.3113 29.2808 36.3668 29.7162C36.4222 30.1516 36.3596 30.5938 36.1856 30.9968C34.7906 34.2518 31.2606 37.0234 27.5889 36.8884Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};
export const LocationIcon = () => {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20 17.5C21.3807 17.5 22.5 16.3807 22.5 15C22.5 13.6193 21.3807 12.5 20 12.5C18.6193 12.5 17.5 13.6193 17.5 15C17.5 16.3807 18.6193 17.5 20 17.5Z"
        fill="var(--primary-color)"
      />
      <path
        d="M20 2.5C13.1078 2.5 7.5 7.86328 7.5 14.4531C7.5 17.5914 8.93047 21.7648 11.7516 26.8578C14.0172 30.9469 16.6383 34.6445 18.0016 36.4844C18.2319 36.7987 18.5331 37.0544 18.8807 37.2306C19.2284 37.4069 19.6126 37.4987 20.0023 37.4987C20.3921 37.4987 20.7763 37.4069 21.1239 37.2306C21.4716 37.0544 21.7728 36.7987 22.0031 36.4844C23.3641 34.6445 25.9875 30.9469 28.2531 26.8578C31.0695 21.7664 32.5 17.593 32.5 14.4531C32.5 7.86328 26.8922 2.5 20 2.5ZM20 20C19.0111 20 18.0444 19.7068 17.2221 19.1573C16.3999 18.6079 15.759 17.827 15.3806 16.9134C15.0022 15.9998 14.9031 14.9945 15.0961 14.0245C15.289 13.0546 15.7652 12.1637 16.4645 11.4645C17.1637 10.7652 18.0546 10.289 19.0245 10.0961C19.9945 9.90315 20.9998 10.0022 21.9134 10.3806C22.827 10.759 23.6079 11.3999 24.1573 12.2221C24.7068 13.0444 25 14.0111 25 15C24.9986 16.3256 24.4713 17.5966 23.5339 18.5339C22.5966 19.4713 21.3256 19.9986 20 20Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};
export const Message2Icon = () => {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M33.125 6.25H6.875C5.71506 6.25124 4.60298 6.71258 3.78278 7.53278C2.96258 8.35298 2.50124 9.46506 2.5 10.625V29.375C2.50124 30.5349 2.96258 31.647 3.78278 32.4672C4.60298 33.2874 5.71506 33.7488 6.875 33.75H33.125C34.2849 33.7488 35.397 33.2874 36.2172 32.4672C37.0374 31.647 37.4988 30.5349 37.5 29.375V10.625C37.4988 9.46506 37.0374 8.35298 36.2172 7.53278C35.397 6.71258 34.2849 6.25124 33.125 6.25ZM32.0172 13.4867L20.7672 22.2367C20.5478 22.4073 20.2779 22.4998 20 22.4998C19.7221 22.4998 19.4522 22.4073 19.2328 22.2367L7.98281 13.4867C7.85064 13.3869 7.73961 13.2618 7.6562 13.1187C7.57278 12.9756 7.51864 12.8174 7.49691 12.6532C7.47519 12.489 7.48631 12.3221 7.52964 12.1622C7.57296 12.0024 7.64763 11.8527 7.7493 11.7219C7.85096 11.5912 7.9776 11.4819 8.12186 11.4005C8.26611 11.3191 8.42511 11.2672 8.5896 11.2478C8.75409 11.2284 8.9208 11.2419 9.08004 11.2874C9.23928 11.333 9.38787 11.4098 9.51719 11.5133L20 19.6664L30.4828 11.5133C30.7449 11.3153 31.0744 11.2285 31.4001 11.2716C31.7257 11.3147 32.0213 11.4842 32.2229 11.7435C32.4245 12.0028 32.516 12.3311 32.4775 12.6573C32.439 12.9835 32.2736 13.2815 32.0172 13.4867Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};
export const ShareIcon = () => {
  return (
    <svg
      width="40"
      height="41"
      viewBox="0 0 40 41"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M23.0057 9.38705C23.0057 6.32038 25.5056 3.83205 28.5856 3.83205C29.317 3.83008 30.0415 3.97222 30.7178 4.25036C31.3942 4.5285 32.0091 4.93718 32.5275 5.45306C33.0458 5.96893 33.4574 6.58188 33.7388 7.25689C34.0202 7.93191 34.1658 8.65575 34.1673 9.38705C34.1673 12.4554 31.6673 14.9437 28.5856 14.9437C27.8467 14.9443 27.115 14.7981 26.4329 14.5137C25.7508 14.2293 25.132 13.8124 24.6123 13.2871L16.8873 18.5471C17.1021 19.6187 16.9966 20.7299 16.584 21.7421L25.054 27.3087C26.0519 26.4956 27.3001 26.0522 28.5873 26.0537C29.3186 26.052 30.0431 26.1943 30.7194 26.4727C31.3956 26.751 32.0104 27.1599 32.5286 27.6759C33.0468 28.1919 33.4583 28.805 33.7394 29.4801C34.0206 30.1552 34.166 30.8791 34.1673 31.6104C34.1673 34.677 31.6673 37.1654 28.5856 37.1654C27.8545 37.1671 27.1301 37.0248 26.454 36.7466C25.7778 36.4684 25.1631 36.0596 24.6449 35.5438C24.1268 35.0279 23.7153 34.4151 23.434 33.7402C23.1527 33.0653 23.0072 32.3415 23.0057 31.6104C23.0044 30.8314 23.1685 30.0611 23.4873 29.3504L15.084 23.8321C14.066 24.7164 12.7624 25.2024 11.414 25.2004C10.6827 25.2021 9.95819 25.0598 9.28193 24.7814C8.60566 24.5031 7.99087 24.0942 7.47267 23.5782C6.95448 23.0622 6.54304 22.4491 6.26186 21.774C5.98069 21.0989 5.8353 20.375 5.83398 19.6437C5.83551 18.9126 5.98107 18.1889 6.26234 17.5139C6.54361 16.839 6.95509 16.2262 7.47326 15.7103C7.99143 15.1945 8.60615 14.7857 9.28231 14.5075C9.95847 14.2293 10.6828 14.087 11.414 14.0887C13.1873 14.0887 14.764 14.9104 15.7857 16.1904L23.274 11.0921C23.0954 10.5414 23.0048 9.96597 23.0057 9.38705Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};
export const AddressLink: React.FC<{ address: string; className?: string }> = ({
  address,
  className = "",
}) => (
  <a
    href={`https://www.google.com/maps/search/?q=${encodeURIComponent(
      address
    )}`}
    target="_blank"
    rel="noopener noreferrer"
    className={` hover:underline ${className}`}
  >
    {address}
  </a>
);
export const EmailLink: React.FC<{ email: string; className?: string }> = ({
  email,
  className = "",
}) => (
  <a href={`mailto:${email}`} className={` hover:underline ${className}`}>
    {email}
  </a>
);

export interface Service {
  icon: React.ReactNode;
  header: string;
  text: string;
  className?: string; // Making className optional
}
export interface ExtendedService extends Service {
  headerAs?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"; // Union type for dynamically choosing header tag
  className?: string;
}

export const Card2: React.FC<ExtendedService> = ({
  icon,
  header,
  text,
  headerAs = "h5", // Default to "h3" if no headerAs is passed
  className,
}) => {
  const HeaderTag = headerAs; // Dynamically set the tag for the header

  return (
    <div
      style={{
        boxShadow: "0px 1.64px 20.5px 0px #21212114",
      }}
      className={`relative select-none bg-white h-full w-[450px] flex gap-4 p-4 rounded-xl ${
        className || ""
      }`}
    >
      <Small type="top" />
      <Small type="bottom" />
      <div className="first bg-themeColor text-white grid place-items-center rounded-md aspect-square size-[3.6rem]">
        <span>{icon}</span>
      </div>
      <div className="second flex flex-col">
        <HeaderTag className="text-base text-primary mb-1 font-semibold">
          {header}
        </HeaderTag>{" "}
        {/* Dynamic header tag */}
        <p className="text-secondary-main text-sm">{text}</p>
      </div>
    </div>
  );
};

const Small: React.FC<{ type: "top" | "bottom" }> = ({ type }) => {
  return (
    <div
      className={` size-6 absolute  border-themeColor ${
        type === "top"
          ? "top-4 border-r-[1.64px] rounded-tr-xl border-t-[1.64px] right-4"
          : "bottom-4 border-l-[1.64px] rounded-bl-xl border-b-[1.64px] left-4"
      }`}
    ></div>
  );
};
